// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ProjectInfo } from '../../../api/types';
import {
  heldLocally,
  leftoverExtras,
  mergeExtras,
  serverStoresExtras,
  readStoredExtras,
  writeStoredExtras,
  type ExtraKey,
  type ExtrasStore,
  type SagsdataExtras,
} from '../../../bygning/sagsdata';

/** localStorage, or undefined where it is absent or access throws (sandbox). */
function browserStore(): ExtrasStore | undefined {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined;
  }
}

export interface SagsdataExtrasState {
  /** What to show: the server's value where it has the key, else the local copy. */
  extras: SagsdataExtras;
  /** The local copy (contract gap 3 fallback for an older server). */
  local: SagsdataExtras;
  /** Keys whose value exists only in this browser. */
  held: ExtraKey[];
  /** Local values for keys the server stores but has empty: the form offers them as unsaved changes. */
  leftover: SagsdataExtras;
  /** Replace the local copy for `projectId` (written to localStorage at once). */
  save: (projectId: string, next: SagsdataExtras) => void;
}

/**
 * The BEK 496 Sagsdata fields (contract gap 3). ruxd stores them since
 * ruxd-6tz; against an older server they are merged from the server record
 * and the copy kept in localStorage under
 * `reusex:sagsdata-extras-v1:<projectId>`. Read on mount and when the
 * project id changes (a record minted by the first save). Once the server
 * stores every key and nothing local is left to carry over, the local copy
 * is removed: localStorage is no longer used. Overblik holds the one
 * instance and hands it to the Sagsdata dialog, so both read the same copy.
 */
export function useSagsdataExtras(project: ProjectInfo | null): SagsdataExtrasState {
  const id = project?.id ?? null;
  const [local, setLocal] = useState<SagsdataExtras>(() => readStoredExtras(browserStore(), id));
  useEffect(() => {
    setLocal(readStoredExtras(browserStore(), id));
  }, [id]);
  const extras = useMemo(() => mergeExtras(project, local), [project, local]);
  const held = useMemo(() => heldLocally(project, local), [project, local]);
  const leftover = useMemo(() => leftoverExtras(project, local), [project, local]);
  const stores = serverStoresExtras(project);
  const nothingLeft = Object.keys(leftover).length === 0;
  const hasLocal = Object.keys(local).length > 0;
  useEffect(() => {
    if (!id || !stores || !nothingLeft || !hasLocal) return;
    writeStoredExtras(browserStore(), id, {});
    setLocal({});
  }, [id, stores, nothingLeft, hasLocal]);
  const save = useCallback((projectId: string, next: SagsdataExtras) => {
    // Storage first: a save that lands after the form unmounted must still persist.
    writeStoredExtras(browserStore(), projectId, next);
    setLocal(next);
  }, []);
  return { extras, local, held, leftover, save };
}
